import { useWindowSize } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { useFeatureFlagStore } from '@/store/featureFlagStore';
import { useProfileStore } from '@/store/profileStore';
import { AI_ASSISTANT_DOCK_WIDTH_MIN, useUiStore } from '@/store/uiStore';
import type { AIAssistantMode } from '@/store/aiAssistantStore';

/**
 * The AI assistant of the page (WI #5769): every page of a project or a
 * template offers its chat — the Sparkles button in the top header and the
 * panel docked next to the page — when the realm has AI and, if the realm
 * keeps an allowlist, the user is on it.
 */
export function useAIAssistantContext() {
  const route = useRoute();
  const featureFlagStore = useFeatureFlagStore();
  const profileStore = useProfileStore();
  const uiStore = useUiStore();
  const { width: windowWidth } = useWindowSize();

  const mode = computed<AIAssistantMode | null>(() => {
    if (route.params.templateId !== undefined) {
      return 'template';
    }

    return route.params.projectId !== undefined ? 'project' : null;
  });

  const contextId = computed(
    () =>
      (route.params.projectId ?? route.params.templateId) as string | undefined,
  );

  const canUseAIAssistant = computed(() => {
    if (!featureFlagStore.hasAI) {
      return false;
    }

    // An allowlist per tenant; without one every user may use it.
    const allowedEmailsEnv = import.meta.env[
      `VITE_${featureFlagStore.tenant}_AI_ASSISTANT_ALLOWED_EMAILS`
    ];
    if (!allowedEmailsEnv) {
      return true;
    }

    const allowedEmails = allowedEmailsEnv
      .split(',')
      .map((email: string) => email.trim().toLowerCase());

    return allowedEmails.includes(profileStore.profile.email.toLowerCase());
  });

  /** The page offers the assistant: a project or template page, AI allowed. */
  const isAvailable = computed(
    () => !!mode.value && !!contextId.value && canUseAIAssistant.value,
  );

  /** Its panel is docked next to the page. */
  const isDockOpen = computed(
    () => isAvailable.value && uiStore.aiAssistantOpen,
  );

  /**
   * The panel is as wide as the user made it, but at most half the window:
   * the page keeps room on a laptop, also after the window shrank.
   */
  const dockMaxWidth = computed(() =>
    Math.max(AI_ASSISTANT_DOCK_WIDTH_MIN, Math.floor(windowWidth.value / 2)),
  );
  const dockWidth = computed(() =>
    Math.min(uiStore.aiAssistantDockWidth, dockMaxWidth.value),
  );

  return {
    mode,
    contextId,
    isAvailable,
    isDockOpen,
    dockWidth,
    dockMaxWidth,
  };
}
